import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ComponentProps } from "react";

// Extended variant type that includes "filled"
type CButtonVariant =
  | "filled"
  | "destructive"
  | "outline"
  | "secondary"
  | "ghost"
  | "link"
  | "icon";

interface CButtonProps extends Omit<ComponentProps<typeof Button>, "variant"> {
  variant?: CButtonVariant;
}

/**
 * CButton - Shared button component with consistent styling
 *
 * Wraps the base Button component with project-wide defaults:
 * - Default variant is "outline"
 * - "filled" variant maps to primary filled button
 * - No shadow (shadow-none)
 * - Consistent border radius (rounded-md)
 *
 * Variants:
 * - "outline" (default) - outlined button
 * - "filled" - filled primary button
 * - "destructive" - filled red button
 * - "secondary" - filled secondary button
 * - "ghost" - transparent with hover
 * - "link" - text link style
 * - "icon" - icon button style
 */
const CButton = ({
  className,
  variant = "outline",
  ...props
}: CButtonProps) => {
  // Map "filled" to the base Button's "default" variant
  const mappedVariant = variant === "filled" ? "default" : variant;

  return (
    <Button
      variant={mappedVariant}
      className={cn("shadow-none rounded", className)}
      {...props}
    />
  );
};

export default CButton;
export { CButton };
export type { CButtonProps, CButtonVariant };
